import {
	PUBLIC_API_HOST,
	PUBLIC_ENVIRONMENT,
	PUBLIC_SENTRY_PROJECT_ID,
	PUBLIC_PACKAGE_NAME,
	PUBLIC_PACKAGE_VERSION,
	PUBLIC_BUILD_SHA,
	PUBLIC_SENTRY_ENABLED,
	PUBLIC_SENTRY_KEY,
	PUBLIC_SENTRY_PROFILES_SAMPLE_RATE,
	PUBLIC_SENTRY_ORG_ID,
	PUBLIC_SENTRY_TRACES_SAMPLE_RATE,
	PUBLIC_SENTRY_TRACE_PROPAGATION_ENABLED
} from '$app/env/public';

import type { Environment } from './types';

// Side-effect free, unlike `#lib/config.js` (which creates a GeoServer at load), so hooks can import it.
export const sentryEnabled = PUBLIC_SENTRY_ENABLED === 'true';
export const sentryDsn = `https://${PUBLIC_SENTRY_KEY}@${PUBLIC_SENTRY_ORG_ID}.ingest.us.sentry.io/${PUBLIC_SENTRY_PROJECT_ID}`;
// Reuses ENVIRONMENT so there is a single source of truth for the environment name
export const sentryEnvironment = PUBLIC_ENVIRONMENT as Environment;
export const sentryTracesSampleRate = Number(PUBLIC_SENTRY_TRACES_SAMPLE_RATE ?? 0);

// Distributed tracing — disabled until nginx is configured to pass sentry-trace/baggage headers.
// Enable per-environment via PUBLIC_SENTRY_TRACE_PROPAGATION_ENABLED=true.
export const sentryTracePropagationEnabled = PUBLIC_SENTRY_TRACE_PROPAGATION_ENABLED === 'true';
export const sentryTracePropagationTargets = [
	/^\//, // same-origin requests (SvelteKit routes, static assets)
	PUBLIC_API_HOST
];

// Sentry's recommended release format is `package@version+build` (semver-aware):
// name + version derive from package.json; the build suffix is the short commit
// SHA (Azure DevOps `BUILD_SOURCEVERSION`) so each deploy is a unique release.
// All derive from build-time env (see `.env`). The suffix is omitted when no SHA
// is available (e.g. a local build).
export const sentryRelease = PUBLIC_BUILD_SHA
	? `${PUBLIC_PACKAGE_NAME}@${PUBLIC_PACKAGE_VERSION}+${PUBLIC_BUILD_SHA}`
	: `${PUBLIC_PACKAGE_NAME}@${PUBLIC_PACKAGE_VERSION}`;
export const sentryProfileSessionSampleRate = Number(PUBLIC_SENTRY_PROFILES_SAMPLE_RATE ?? 0.01);

// Requests excluded from span creation — high-volume or third-party URLs that add noise.
// Matched as substrings against the full request URL.
export const sentryIgnoredRequestUrls = [
	'google-analytics.com',
	'googletagmanager.com',
	'api.maptiler.com/tiles/', // tile fetches — high volume, not worth tracing
	'cdn.maptiler.com/' // basemap assets
];

// Errors that are noise: browser extensions, known browser quirks, Sentry's own wrappers
export const sentryIgnoreErrors = [
	// Cross-origin scripts have their error details stripped to "Script error."
	// Our bundles are same-origin so never appear here — this is purely extension
	// noise. If assets ever move to a CDN domain, add crossorigin="anonymous" +
	// CORS headers first.
	'Script error.',
	'ResizeObserver loop limit exceeded',
	'ResizeObserver loop completed with undelivered notifications',
	'Non-Error exception captured',
	'Non-Error promise rejection captured'
];
